import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { MAX_VISIBLE_TOASTS, type Toast, type ToastStore, useToasts } from "@/lib/toast";

import { CopyReferenceButton } from "./CopyReferenceButton";

const ICON = { success: CircleCheck, error: CircleAlert, info: Info } as const;

function ToastItem({ toast, store }: { toast: Toast; store: ToastStore }) {
  const { t } = useTranslation();
  const [left, setLeft] = useState(toast.ttl);
  const [undoing, setUndoing] = useState(false);

  // countdown starts when the toast becomes visible (queued ones wait their turn)
  useEffect(() => {
    if (toast.ttl === null) {
      return;
    }
    const startedAt = Date.now();
    const total = toast.ttl;
    const tick = setInterval(() => {
      const remaining = total - (Date.now() - startedAt);
      if (remaining <= 0) {
        clearInterval(tick);
        store.dismiss(toast.id);
      } else {
        setLeft(remaining);
      }
    }, 250);
    return () => clearInterval(tick);
  }, [toast.id, toast.ttl, store]);

  const Icon = ICON[toast.kind];
  const seconds = left === null ? null : Math.max(1, Math.ceil(left / 1000));

  const onUndo = async () => {
    if (!toast.undo || undoing) {
      return;
    }
    setUndoing(true);
    try {
      await toast.undo();
      store.dismiss(toast.id);
      store.success(t("toast.undone"));
    } catch {
      store.dismiss(toast.id);
      store.error(t("toast.undo_failed"));
    }
  };

  // the <li> stays a plain list item (axe `list` rule); the message box inside carries the role
  return (
    <li className="w-full">
      <div
        data-slot="toast"
        data-kind={toast.kind}
        role={toast.kind === "error" ? "alert" : "status"}
        className={cn(
          "pointer-events-auto flex w-full items-start gap-3 rounded-lg border bg-background p-3 shadow-brand",
          toast.kind === "error" ? "border-status-problem" : "border-foreground",
        )}
      >
        <Icon
          className={cn(
            "mt-0.5 size-5 shrink-0",
            toast.kind === "error"
              ? "text-status-problem"
              : toast.kind === "success"
                ? "text-[#047857]"
                : "",
          )}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-sm font-medium break-words">{toast.message}</p>
          {toast.hint ? <p className="text-xs text-muted-foreground">{toast.hint}</p> : null}
          {toast.requestId ? <CopyReferenceButton requestId={toast.requestId} /> : null}
        </div>
        {toast.undo ? (
          <button
            type="button"
            onClick={() => void onUndo()}
            disabled={undoing}
            className="min-h-touch shrink-0 rounded-md border px-3 text-sm font-semibold hover:bg-accent"
          >
            {seconds === null ? t("toast.undo") : t("toast.undo_seconds", { seconds })}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            store.dismiss(toast.id);
          }}
          aria-label={t("toast.dismiss")}
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-md hover:bg-accent"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </li>
  );
}

/** Bottom-right on desktop, above the bottom navigation on tablets/phones. */
export function Toaster({ store }: { store: ToastStore }) {
  const { t } = useTranslation();
  const toasts = useToasts(store);
  const visible = toasts.slice(0, MAX_VISIBLE_TOASTS);
  const waiting = toasts.length - visible.length;
  return (
    <section
      aria-label={t("toast.region")}
      className="pointer-events-none fixed inset-x-3 bottom-[calc(var(--admin-bottom-nav,0px)+0.75rem)] z-[60] flex flex-col items-end gap-2 sm:left-auto sm:w-96"
    >
      <ol aria-live="polite" className="flex w-full flex-col gap-2">
        {visible.map((toast) => (
          <ToastItem key={toast.id} toast={toast} store={store} />
        ))}
      </ol>
      {waiting > 0 ? (
        <p className="pointer-events-auto rounded bg-foreground px-2 py-1 text-xs text-background">
          {t("toast.more", { count: waiting })}
        </p>
      ) : null}
    </section>
  );
}
